import React from 'react';
import { Clock, MessageCircle, ShieldCheck } from 'lucide-react';
import { Icon, IconComponent } from '@/components/common';

export type BrandPanelVariant = 'sign-in' | 'find-plan';

interface PanelItem {
  /** An icon, or a step number for the "Find plan" steps. */
  icon?: IconComponent;
  step?: number;
  title: string;
  text: string;
}

const CONTENT: Record<BrandPanelVariant, { eyebrow: string; lines: [string, string]; body: string; items: PanelItem[]; chips?: string[] }> = {
  'sign-in': {
    eyebrow: 'Your protection, in one place',
    lines: ['Every purchase,', 'protected.'],
    body: 'Your account is already set up if you bought a Protega plan with any brand. Just sign in with the number or email you used at checkout.',
    items: [
      { icon: ShieldCheck, title: 'All your plans together', text: 'Cover, dates and certificates for every order' },
      { icon: MessageCircle, title: 'Claims on WhatsApp', text: 'Share a photo, we handle repair or replacement' },
      { icon: Clock, title: 'Live claim tracking', text: 'Updates at every step until it’s resolved' },
    ],
    chips: ['Luggage', 'Appliances', 'Furniture', 'Gadgets', 'Watches', 'Fashion'],
  },
  'find-plan': {
    eyebrow: 'Can’t sign in?',
    lines: ['We’ll find your', 'plan for you.'],
    body: 'Changed your number, or used a different one at checkout? Share your order details and we’ll link your plan to a number or email you can use from now on.',
    items: [
      { step: 1, title: 'Share your order', text: 'Brand, order ID and invoice' },
      { step: 2, title: 'Verify your contact', text: 'A one-time code on mobile or email' },
      { step: 3, title: 'Plan linked', text: 'Sign in any time with your new details' },
    ],
  },
};

/** Right-hand panel on desktop sign-in screens (Figma "Login panel"). Dark in both themes. */
const BrandPanel: React.FC<{ variant: BrandPanelVariant }> = ({ variant }) => {
  const { eyebrow, lines, body, items, chips } = CONTENT[variant];
  return (
    <aside className="tw-relative tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-center tw-gap-7 tw-overflow-hidden tw-rounded-l-[32px] tw-bg-ds-panel-bg tw-px-[72px] tw-py-16">
      {/* Soft brand glow in the top-right corner. */}
      <span
        aria-hidden
        className="tw-pointer-events-none tw-absolute tw--right-[246px] tw--top-[220px] tw-h-[520px] tw-w-[520px] tw-rounded-full tw-blur-[90px]"
        style={{ background: 'radial-gradient(circle, var(--ds-panel-glow) 0%, transparent 70%)' }}
      />
      <div className="tw-relative tw-flex tw-flex-col tw-gap-4">
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-[0.66px] tw-text-ds-panel-accent">{eyebrow}</p>
        <h2 className="tw-text-[48px] tw-font-bold tw-leading-[56px] tw-tracking-[-0.96px] tw-text-ds-panel-text">
          {lines[0]}
          <br />
          <span className="tw-text-ds-panel-accent">{lines[1]}</span>
        </h2>
        <p className="tw-text-[18px] tw-leading-[27px] tw-text-ds-panel-muted">{body}</p>
      </div>
      <ul className="tw-relative tw-flex tw-flex-col tw-gap-[14px]">
        {items.map((item) => (
          <li
            key={item.title}
            className="tw-flex tw-items-center tw-gap-4 tw-rounded-[14px] tw-border tw-border-ds-panel-border tw-bg-ds-panel-card tw-px-5 tw-py-[18px]"
          >
            <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-panel-icon-bg tw-text-ds-panel-icon">
              {item.icon ? <Icon icon={item.icon} size={20} /> : <span className="tw-text-[14px] tw-font-semibold tw-leading-5">{item.step}</span>}
            </span>
            <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
              <span className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-panel-text">{item.title}</span>
              <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-panel-muted">{item.text}</span>
            </span>
          </li>
        ))}
      </ul>
      {chips && (
        <ul className="tw-relative tw-flex tw-flex-wrap tw-gap-2" aria-label="Product categories">
          {chips.map((chip) => (
            <li key={chip} className="tw-rounded-full tw-border tw-border-ds-panel-border tw-px-3 tw-py-1.5 tw-text-[12px] tw-font-medium tw-leading-4 tw-text-ds-panel-muted">
              {chip}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

export default BrandPanel;
